'use client';

import { RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FRONTEND_RELEASE_IDLE_TIMEOUT_MS,
  FRONTEND_RELEASE_RECONNECT_NOTICE_DELAY_MS,
  FRONTEND_RELEASE_SSE_PATH,
  buildFrontendReleaseDismissStorageKey,
  normalizeFrontendBuildId,
  parseFrontendReleaseEnvelope,
  resolveFrontendReleaseReconnectDelayMs,
  shouldReloadFrontendRelease,
} from '@/shared/deployment/frontend-release';
import { zIndex } from '@/shared/ui/z-index';

interface FrontendReleaseRefreshManagerProps {
  currentBuildId?: string | null;
}

const USER_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input', 'scroll'] as const;

/** 열린 화면을 유지하면서 새 프런트엔드 릴리스를 감지하고 유휴 시 갱신합니다. */
export function FrontendReleaseRefreshManager({ currentBuildId }: FrontendReleaseRefreshManagerProps) {
  const normalizedCurrentBuildId = useMemo(() => normalizeFrontendBuildId(currentBuildId), [currentBuildId]);
  const [targetBuildId, setTargetBuildId] = useState<string | null>(null);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);
  const [showReconnectNotice, setShowReconnectNotice] = useState(false);
  const [isReconnectNoticeDismissed, setIsReconnectNoticeDismissed] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectNoticeTimerRef = useRef<number | null>(null);
  const idleReloadTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const targetBuildIdRef = useRef<string | null>(null);
  const lastActivityAtRef = useRef(0);
  const isReloadingRef = useRef(false);
  const isUpdateNoticeDismissed = Boolean(targetBuildId && dismissedBuildId === targetBuildId);

  /** 현재 문서를 한 번만 새로고침합니다. */
  const reloadPage = useCallback(() => {
    if (isReloadingRef.current) {
      return;
    }

    isReloadingRef.current = true;
    window.location.reload();
  }, []);

  /** 대상 릴리스가 있으면 마지막 활동 시각을 기준으로 자동 갱신을 예약합니다. */
  const scheduleIdleReload = useCallback(() => {
    if (idleReloadTimerRef.current !== null) {
      window.clearTimeout(idleReloadTimerRef.current);
      idleReloadTimerRef.current = null;
    }

    if (!targetBuildIdRef.current || isReloadingRef.current) {
      return;
    }

    const now = Date.now();
    if (shouldReloadFrontendRelease(lastActivityAtRef.current, now)) {
      reloadPage();
      return;
    }

    const remainingIdleMs = Math.max(0, FRONTEND_RELEASE_IDLE_TIMEOUT_MS - (now - lastActivityAtRef.current));
    idleReloadTimerRef.current = window.setTimeout(reloadPage, remainingIdleMs);
  }, [reloadPage]);

  useEffect(() => {
    lastActivityAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!normalizedCurrentBuildId) {
      return;
    }

    let isStopped = false;

    /** 대기 중인 SSE 재연결 timer를 제거합니다. */
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    /** 연결 오류가 일정 시간 지속될 때만 사용자 안내를 표시합니다. */
    const scheduleReconnectNotice = () => {
      if (reconnectNoticeTimerRef.current !== null) {
        return;
      }

      reconnectNoticeTimerRef.current = window.setTimeout(() => {
        reconnectNoticeTimerRef.current = null;
        if (!isStopped && eventSourceRef.current?.readyState !== EventSource.OPEN) {
          setShowReconnectNotice(true);
        }
      }, FRONTEND_RELEASE_RECONNECT_NOTICE_DELAY_MS);
    };

    /** 현재 SSE를 닫고 다음 재연결을 예약합니다. */
    const scheduleReconnect = (connect: () => void) => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      clearReconnectTimer();
      scheduleReconnectNotice();

      const reconnectDelayMs = resolveFrontendReleaseReconnectDelayMs(reconnectAttemptRef.current);
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = window.setTimeout(connect, reconnectDelayMs);
    };

    /** 배포 버전 SSE를 새로 연결하고 release envelope를 처리합니다. */
    const connect = () => {
      if (isStopped || !navigator.onLine) {
        return;
      }

      clearReconnectTimer();
      eventSourceRef.current?.close();
      const eventSource = new EventSource(FRONTEND_RELEASE_SSE_PATH);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        if (isStopped || eventSourceRef.current !== eventSource) {
          return;
        }

        reconnectAttemptRef.current = 0;
        if (reconnectNoticeTimerRef.current !== null) {
          window.clearTimeout(reconnectNoticeTimerRef.current);
          reconnectNoticeTimerRef.current = null;
        }
        setShowReconnectNotice(false);
        setIsReconnectNoticeDismissed(false);
      };

      eventSource.onmessage = event => {
        if (isStopped || eventSourceRef.current !== eventSource) {
          return;
        }

        try {
          const envelope = parseFrontendReleaseEnvelope(JSON.parse(event.data) as unknown);
          if (!envelope || envelope.type !== 'release') {
            return;
          }

          if (envelope.buildId === normalizedCurrentBuildId) {
            setTargetBuildId(null);
            setDismissedBuildId(null);
            return;
          }

          const storageKey = buildFrontendReleaseDismissStorageKey(envelope.buildId);
          setTargetBuildId(envelope.buildId);
          setDismissedBuildId(storageKey && window.sessionStorage.getItem(storageKey) === '1' ? envelope.buildId : null);
        } catch (error) {
          console.warn('[frontend-release] 배포 버전 SSE 메시지를 해석하지 못했습니다.', error);
        }
      };

      eventSource.onerror = () => {
        if (isStopped || eventSourceRef.current !== eventSource) {
          return;
        }

        scheduleReconnect(connect);
      };
    };

    /** 네트워크 복귀 시 지연 없이 배포 버전 SSE를 복구합니다. */
    const handleOnline = () => {
      reconnectAttemptRef.current = 0;
      connect();
    };

    /** 다시 열린 탭의 SSE가 닫혀 있으면 즉시 재연결합니다. */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && eventSourceRef.current?.readyState !== EventSource.OPEN) {
        connect();
      }
    };

    connect();
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isStopped = true;
      clearReconnectTimer();
      if (reconnectNoticeTimerRef.current !== null) {
        window.clearTimeout(reconnectNoticeTimerRef.current);
        reconnectNoticeTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [normalizedCurrentBuildId]);

  useEffect(() => {
    targetBuildIdRef.current = targetBuildId;
    if (!targetBuildId) {
      if (idleReloadTimerRef.current !== null) {
        window.clearTimeout(idleReloadTimerRef.current);
        idleReloadTimerRef.current = null;
      }
      return;
    }

    scheduleIdleReload();
  }, [scheduleIdleReload, targetBuildId]);

  useEffect(() => {
    /** 키보드·포인터·입력·스크롤을 실제 사용자 활동으로 기록합니다. */
    const recordUserActivity = () => {
      lastActivityAtRef.current = Date.now();
      scheduleIdleReload();
    };

    /** 숨겨졌던 탭이 다시 보이면 누적된 유휴 시간을 즉시 반영합니다. */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleIdleReload();
      }
    };

    USER_ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, recordUserActivity, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      USER_ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, recordUserActivity, { capture: true }));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (idleReloadTimerRef.current !== null) {
        window.clearTimeout(idleReloadTimerRef.current);
        idleReloadTimerRef.current = null;
      }
    };
  }, [scheduleIdleReload]);

  /** 현재 대상 릴리스의 안내만 세션 동안 숨깁니다. */
  const dismissUpdateNotice = () => {
    const storageKey = buildFrontendReleaseDismissStorageKey(targetBuildId);
    if (storageKey) {
      window.sessionStorage.setItem(storageKey, '1');
    }
    setDismissedBuildId(targetBuildId);
  };

  if (targetBuildId && !isUpdateNoticeDismissed) {
    return (
      <div
        className="fixed bottom-[calc(16px_+_var(--app-safe-bottom-offset,0px))] left-1/2 w-[min(calc(100vw-32px),440px)] -translate-x-1/2 rounded-lg border border-blue-200 bg-white/95 p-4 shadow-xl backdrop-blur"
        role="status"
        aria-live="polite"
        style={{ zIndex: zIndex.realtimeNotification }}>
        <div className="flex items-start gap-3">
          <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">새 버전이 배포되었습니다.</p>
            <p className="mt-1 text-xs leading-5 text-slate-600">5분간 조작이 없으면 화면을 자동으로 새로고침합니다.</p>
            <button type="button" className="mt-3 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700" onClick={reloadPage}>
              지금 새로고침
            </button>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="배포 안내 닫기"
            onClick={dismissUpdateNotice}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (showReconnectNotice && !isReconnectNoticeDismissed) {
    return (
      <div
        className="fixed bottom-[calc(16px_+_var(--app-safe-bottom-offset,0px))] left-1/2 w-[min(calc(100vw-32px),440px)] -translate-x-1/2 rounded-lg border border-amber-200 bg-white/95 p-4 shadow-xl backdrop-blur"
        role="status"
        aria-live="polite"
        style={{ zIndex: zIndex.realtimeNotification }}>
        <div className="flex items-start gap-3">
          <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-amber-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">서비스 업데이트를 확인하고 있습니다.</p>
            <p className="mt-1 text-xs leading-5 text-slate-600">현재 화면을 유지한 채 다시 연결합니다.</p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="재연결 안내 닫기"
            onClick={() => setIsReconnectNoticeDismissed(true)}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
